import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Button, SearchField, Select } from "@legends/ui";
import { fetchAudit, type AuditEntry } from "../api";
import { AsyncState, monoLabelStyle } from "../sections";
import { AUDIT_ACTION_LABEL, auditLine } from "./auditLabels";

// The audit log (ADR 0160): the append-only record of every consequential
// back-office action — impersonation, revocations, role/super-admin changes,
// cave/event/ticket deletions, recording re-arms. Super-admin only. Filter by
// action or actor; newest first.

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  ...Object.entries(AUDIT_ACTION_LABEL).map(([value, label]) => ({ value, label })),
];

const cellStyle: CSSProperties = { padding: "10px 12px", verticalAlign: "top", fontSize: "var(--fs-sm)" };
const headStyle: CSSProperties = {
  ...monoLabelStyle,
  textAlign: "left",
  padding: "8px 12px",
  borderBottom: "1px solid var(--border)",
};

export default function AuditScreen() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState(false);
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");

  const load = useCallback(() => {
    setError(false);
    setEntries(null);
    fetchAudit({ action: action || undefined, actor: actor.trim() || undefined, limit: 200 })
      .then(setEntries)
      .catch(() => {
        setEntries(null);
        setError(true);
      });
  }, [action, actor]);

  useEffect(() => load(), [load]);

  return (
    <div style={{ maxWidth: 960 }}>
      <p style={{ margin: "0 0 16px", fontSize: "var(--fs-md)", color: "var(--text-2)", lineHeight: 1.5 }}>
        Every consequential back-office action, newest first. Impersonation, entitlement revocations, role and
        super-admin changes, cave / event / ticket deletions, and recording re-arms are recorded here with the actor,
        the reason, and whether it happened while impersonating.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
        <label style={{ display: "grid", gap: 4 }}>
          <span style={monoLabelStyle}>Action</span>
          <Select aria-label="Filter by action" value={action} onChange={setAction} options={ACTION_OPTIONS} />
        </label>
        <SearchField value={actor} onChange={setActor} placeholder="Filter by actor username…" width={260} />
        <Button size="sm" variant="muted" onClick={load}>
          Refresh
        </Button>
      </div>

      {error ? (
        <AsyncState status="error" errorText="Couldn't load the audit log." onRetry={load} />
      ) : entries === null ? (
        <AsyncState status="loading" />
      ) : entries.length === 0 ? (
        <AsyncState status="empty" emptyText="No matching audit entries." />
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", background: "var(--surface-2)" }}>
            <thead>
              <tr>
                <th style={headStyle}>When</th>
                <th style={headStyle}>Action</th>
                <th style={headStyle}>Actor</th>
                <th style={headStyle}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ ...cellStyle, whiteSpace: "nowrap", color: "var(--text-3)", fontFamily: "var(--font-mono)", fontSize: "var(--fs-2xs)" }}>
                    {new Date(e.at).toLocaleString()}
                  </td>
                  <td style={{ ...cellStyle, whiteSpace: "nowrap", color: "var(--text-1)" }}>
                    {AUDIT_ACTION_LABEL[e.action] ?? e.action}
                    {e.impersonating && (
                      <span
                        style={{
                          marginLeft: 6,
                          fontSize: "var(--fs-3xs)",
                          textTransform: "uppercase",
                          letterSpacing: "var(--ls-label)",
                          color: "var(--warn)",
                        }}
                      >
                        · imp
                      </span>
                    )}
                  </td>
                  <td style={{ ...cellStyle, whiteSpace: "nowrap", color: "var(--text-2)" }}>{e.actor || "—"}</td>
                  <td style={{ ...cellStyle, color: "var(--text-2)" }}>{auditLine(e)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
